import { useCallback, useEffect, useRef, useState } from "react";
import { GridEditModal } from "../editSearch/gridEditModal.tsx";
import { useToast } from "../../hooks/useToast.ts";
import {
    sendKioskCommandVideo,
    getVideoStorageName,
    getKioskVideos,
    getCustomerEventReturns,
    CustomerEventRelation,
} from "../../api/specialized.ts";
import { getFileAccessToken } from "../../api/fileApi.ts";
import { formatInstant, parseUtcTimestamp } from "../../util/serverTime.ts";

interface VideoModalProps {
    open: boolean;
    kioskID: number;
    instantID: string;
    /** InstantID for the identity camera (from CAMERA event's Extra field) */
    camIdent?: string;
    /** List the rental and its returns (S_Customer_Event.RelationID) so their
     *  videos can be opened from here. Customer events only. */
    showReturns?: boolean;
    onClose: () => void;
}

/** The session whose videos are showing: the one the modal was opened for, or
 *  a rental/return picked from the Returns list. */
interface ActiveSession {
    kioskID: number;
    instantID: string;
    camIdent?: string;
}

interface VideoEntry {
    title: string;
    cam: string | null;
    url: string;
    /** FileAPI file-token from the list response (key into filetoken table). */
    token?: string;
    /** Short-lived JWT for the v2/* routes. Embedded as ?access_token= on the video src so
     *  `<video>` gets HTTP Range-streaming (industry signed-URL pattern for media elements). */
    accessToken?: string;
    isStream?: boolean;
}

// FileAPI lives at the same BASE_URL prefix as AdminServerService (e.g. /beta/ for the
// beta deployment, / for live) — mirrors buildApiUrl in api/client.ts so beta WebAdminCC
// hits the beta FileAPI deployment under fileapi.instantsystems.se/beta.
const FILEAPI_BASE = `https://fileapi.instantsystems.se${import.meta.env.BASE_URL.replace(/\/+$/, "")}/`;

export function VideoModal({ open, kioskID, instantID: openedID, camIdent: openedCam, showReturns, onClose }: VideoModalProps) {
    const toast = useToast();
    const videoRef = useRef<HTMLVideoElement>(null);

    const [active, setActive] = useState<ActiveSession>({ kioskID, instantID: openedID, camIdent: openedCam });
    useEffect(() => {
        setActive({ kioskID, instantID: openedID, camIdent: openedCam });
    }, [kioskID, openedID, openedCam]);
    const instantID = active.instantID;
    const camIdent = active.camIdent;

    const [relations, setRelations] = useState<CustomerEventRelation[]>([]);
    // Dedupe by the opened InstantID — StrictMode double-mount.
    const relationsKeyRef = useRef("");
    useEffect(() => {
        if (!(open && showReturns && openedID)) return;
        if (relationsKeyRef.current === openedID) return;
        relationsKeyRef.current = openedID;
        setRelations([]);
        getCustomerEventReturns(openedID)
            .then(res => { if (!res.bError) setRelations(res.oData ?? []); })
            .catch(() => { /* the list is optional; the video itself still loads */ });
    }, [open, showReturns, openedID]);

    const [videos, setVideos] = useState<VideoEntry[]>([]);
    const [selectedIdx, setSelectedIdx] = useState(0);
    const [loading, setLoading] = useState(false);
    const [notFound, setNotFound] = useState(false);
    const [isFaceCam, setIsFaceCam] = useState(true);
    const [requesting, setRequesting] = useState(false);
    const [requested, setRequested] = useState(false);
    const [requestStatus, setRequestStatus] = useState<{ ok: boolean; message: string } | null>(null);

    /**
     * JWT-flavoured FileAPI list call. Mints a short-lived access token bound to this
     * InstantID, calls the v2 route with `Authorization: Bearer`, and returns the JWT
     * alongside the result so the video element can attach `?access_token=...` to its src.
     * Returns null on any failure — caller falls through to legacy paths.
     */
    const fetchFileApiVideosJwt = useCallback(async (sid: string): Promise<{ token: string; filenames: string[]; accessToken: string } | null> => {
        try {
            const accessToken = await getFileAccessToken(sid);
            const resp = await fetch(`${FILEAPI_BASE}api/video/v2/${encodeURIComponent(sid)}`, {
                headers: { Authorization: `Bearer ${accessToken}` },
            });
            if (!resp.ok) return null;
            const data = await resp.json();
            if (!data?.token || !data.filenames?.length) return null;
            return { token: data.token, filenames: data.filenames, accessToken };
        } catch {
            return null;
        }
    }, []);

    const loadVideos = useCallback(async (sid: string) => {
        setLoading(true);
        setNotFound(false);
        setVideos([]);
        setSelectedIdx(0);

        // 1. Try FileAPI (JWT path).
        const jwtResult = await fetchFileApiVideosJwt(sid);
        if (jwtResult) {
            const entries = buildVideoEntries(jwtResult.filenames, true, jwtResult.token, jwtResult.accessToken);
            setVideos(entries);
            setSelectedIdx(0);
            setLoading(false);
            return;
        }

        // 2. Fallback: WebGetKioskVideos
        try {
            const res = await getKioskVideos(sid);
            if (!res.bError && res.oData?.length) {
                const entries = buildVideoEntries(res.oData, false, "", "");
                setVideos(entries);
                setSelectedIdx(0);
                setLoading(false);
                return;
            }
        } catch { /* swallow */ }

        // 3. Fallback: WebGetVideoStorageName
        try {
            const res = await getVideoStorageName();
            if (!res.bError && res.oData) {
                const url = `${res.oData}${active.kioskID}/${sid}.avi`;
                setVideos([{ title: "Video", cam: null, url, isStream: false }]);
                setSelectedIdx(0);
                setLoading(false);
                return;
            }
        } catch { /* swallow */ }

        setNotFound(true);
        setLoading(false);
    }, [fetchFileApiVideosJwt, active.kioskID]);

    // Dedupe by instantID — StrictMode's dev double-mount otherwise fires
    // the whole 3-step fallback chain twice (FileAPI + getKioskVideos +
    // getVideoStorageName). Reused in-flight Promise collapses them.
    const videoLoadRef = useRef<{ id: string; promise: Promise<void> } | null>(null);
    useEffect(() => {
        if (!(open && instantID)) return;
        if (videoLoadRef.current?.id === instantID) return;
        const promise = loadVideos(instantID);
        videoLoadRef.current = { id: instantID, promise };
        void promise.finally(() => {
            if (videoLoadRef.current?.id === instantID) videoLoadRef.current = null;
        });
    }, [open, instantID, loadVideos]);

    // Reset the "already-requested" latch when the modal closes so a later
    // re-open lets the user request again.
    useEffect(() => {
        if (!open) {
            setRequested(false);
            setRequestStatus(null);
        }
    }, [open]);

    const handleUploadRequest = async () => {
        if (requesting || requested) return;
        setRequesting(true);
        setRequestStatus(null);
        try {
            const res = await sendKioskCommandVideo(active.kioskID, "UPLOADVIDEOS", 0, instantID);
            const ok = !res.bError && !!res.oData;
            const message = ok
                ? "Video requested. Check back in a few minutes."
                : "Failed to send upload command to kiosk.";
            setRequestStatus({ ok, message });
            if (ok) setRequested(true);
            toast.toast(message, ok ? "success" : "error");
        } finally {
            setRequesting(false);
        }
    };

    const selectSession = (r: CustomerEventRelation) => {
        if (r.InstantID === instantID) return;
        setActive({ kioskID: r.KioskID, instantID: r.InstantID, camIdent: r.CamIdent || undefined });
        setIsFaceCam(true);
        // "Get Video" is per session — the latch from the previous one doesn't apply.
        setRequested(false);
        setRequestStatus(null);
    };

    const rental = relations.find(r => !r.IsReturn);
    const returns = relations.filter(r => r.IsReturn);

    const handleRefresh = () => {
        loadVideos(isFaceCam ? instantID : instantID.replace("K", "K(2)"));
    };

    const handleRefreshIdentityCam = () => {
        if (camIdent) loadVideos(camIdent);
    };

    const handleSwap = () => {
        const next = !isFaceCam;
        setIsFaceCam(next);
        const sid = next ? instantID : instantID.replace("K", "K(2)");
        loadVideos(sid);
    };

    const currentVideo = videos[selectedIdx];
    const videoSrc = currentVideo
        ? currentVideo.isStream
            // v2 route + ?access_token= so the browser does HTTP Range/seek on `<video>` —
            // browser media elements can't set Authorization headers (industry signed-URL
            // pattern). Token is short-lived (5 min) so leak surface via URL/logs is small.
            ? `${FILEAPI_BASE}api/video/v2/${currentVideo.token}/${currentVideo.url}?access_token=${encodeURIComponent(currentVideo.accessToken ?? "")}`
            : currentVideo.url
        : "";

    return (
        <GridEditModal
            open={open}
            title="Video"
            wide
            readOnly
            cancelLabel="Close"
            onClose={onClose}
            onSave={() => { /* read-only */ }}
        >
                {/* Only when the event has returns (or is one). The server answers an empty
                    list for every event without a chain, so the dialog is unchanged there. */}
                {relations.length > 0 && (
                    <div style={{
                        display: "flex", gap: "var(--space-2)", marginBottom: "var(--space-3)", flexWrap: "wrap", alignItems: "center",
                        padding: "var(--space-2) var(--space-3)", borderRadius: "var(--radius-1)",
                        border: "1px solid var(--color-border-default)", background: "var(--color-bg-muted)",
                        fontSize: "var(--text-sm)",
                    }}>
                        {rental && (
                            <>
                                <strong>Rental</strong>
                                <SessionChip relation={rental} active={rental.InstantID === instantID} onSelect={selectSession} />
                            </>
                        )}
                        <strong style={{ marginLeft: rental ? "var(--space-2)" : undefined }}>Returns</strong>
                        {returns.map(r => (
                            <SessionChip key={r.InstantID} relation={r} active={r.InstantID === instantID} onSelect={selectSession} />
                        ))}
                    </div>
                )}
                <div style={{ display: "flex", gap: "var(--space-2)", marginBottom: "var(--space-2)", flexWrap: "wrap", alignItems: "center" }}>
                    {(() => {
                        const disabled = requesting || requested;
                        return (
                            <button
                                style={{ ...btnStyle("#c0392b"), opacity: disabled ? 0.6 : 1, cursor: disabled ? "default" : "pointer" }}
                                onClick={handleUploadRequest}
                                disabled={disabled}
                                title={requested ? "Video already requested for this session" : undefined}
                            >
                                {requesting ? "Requesting…" : requested ? "Requested" : "Get Video"}
                            </button>
                        );
                    })()}
                    <button style={btnStyle()} onClick={handleRefresh}>Refresh</button>
                    {camIdent && (
                        <button style={btnStyle()} onClick={handleRefreshIdentityCam}>Refresh Identity Cam</button>
                    )}
                    <button style={btnStyle()} onClick={handleSwap}>Swap Webcam</button>
                </div>
                {requestStatus && (
                    <div
                        role="status"
                        style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "var(--space-2)",
                            marginBottom: "var(--space-3)",
                            padding: "var(--space-2) var(--space-3)",
                            borderRadius: "var(--radius-1)",
                            border: `1px solid var(--color-${requestStatus.ok ? "success" : "error"})`,
                            color: `var(--color-${requestStatus.ok ? "success" : "error"})`,
                            background: "var(--color-bg-muted)",
                            fontSize: "var(--text-sm)",
                        }}
                    >
                        <span style={{ flex: 1 }}>{requestStatus.message}</span>
                        <button
                            type="button"
                            onClick={() => setRequestStatus(null)}
                            aria-label="Dismiss"
                            style={{
                                background: "transparent",
                                border: "none",
                                color: "inherit",
                                cursor: "pointer",
                                fontSize: "var(--text-base)",
                                lineHeight: 1,
                                padding: "0 var(--space-1)",
                            }}
                        >
                            ×
                        </button>
                    </div>
                )}

                {loading && <div style={{ padding: "var(--space-4)", textAlign: "center" }}>Loading...</div>}

                {notFound && (
                    <div style={{ padding: "var(--space-4)", textAlign: "center", color: "var(--color-text-muted)" }}>
                        Video has not been uploaded yet. Please try again in 5–10 minutes.
                    </div>
                )}

                {!loading && videos.length > 0 && (
                    <div style={{ display: "flex", gap: "var(--space-3)" }}>
                        {videos.length > 1 && (
                            <div style={{
                                width: 170, minWidth: 170, maxHeight: 500, overflowY: "auto",
                                border: "1px solid var(--color-border-default)", borderRadius: "var(--radius-1)",
                            }}>
                                {videos.map((v, i) => (
                                    <div
                                        key={i}
                                        onClick={() => setSelectedIdx(i)}
                                        style={{
                                            padding: "var(--space-2)",
                                            cursor: "pointer",
                                            borderBottom: "1px solid var(--color-border-default)",
                                            background: i === selectedIdx ? "var(--color-bg-muted)" : undefined,
                                            fontSize: "var(--text-sm)",
                                        }}
                                    >
                                        {v.cam && <div style={{ fontWeight: 600, fontSize: "var(--text-xs)" }}>Camera: {v.cam}</div>}
                                        <div>{v.title}</div>
                                    </div>
                                ))}
                            </div>
                        )}
                        <div style={{ flex: 1 }}>
                            <video
                                ref={videoRef}
                                key={videoSrc}
                                src={videoSrc}
                                controls
                                autoPlay
                                style={{ width: "100%", maxHeight: 500, background: "#000" }}
                            />
                        </div>
                    </div>
                )}
        </GridEditModal>
    );
}

/* ── helpers ─────────────────────────────────────────── */

/** One rental/return session in the Returns strip. Started is UTC without a
 *  zone, like every S_Customer_Event timestamp. */
function SessionChip({ relation, active, onSelect }: {
    relation: CustomerEventRelation;
    active: boolean;
    onSelect: (r: CustomerEventRelation) => void;
}) {
    const started = formatInstant(parseUtcTimestamp(relation.Started)) || relation.Started;
    return (
        <button
            type="button"
            onClick={() => onSelect(relation)}
            aria-pressed={active}
            title={`${relation.InstantID} — kiosk ${relation.KioskID}`}
            style={{
                ...btnStyle(),
                background: active ? "var(--color-primary-muted)" : "var(--color-bg)",
                borderColor: active ? "var(--color-border-focus)" : undefined,
                fontWeight: active ? 600 : undefined,
                cursor: active ? "default" : "pointer",
            }}
        >
            {started}
        </button>
    );
}

function buildVideoEntries(filenames: string[], isStream: boolean, token: string, accessToken: string): VideoEntry[] {
    const entries: VideoEntry[] = [];
    for (let i = 0; i < filenames.length; i++) {
        const fname = filenames[i]!;
        const match = /([A-Z0-9]{12})_[0-9]{8}_([0-9]{6})_[A-Z0-9]{0,4}\.mkv/i.exec(fname);
        if (match) {
            const time = match[2]!;
            entries.push({
                title: `${time.substring(0, 2)}:${time.substring(2, 4)}:${time.substring(4, 6)}`,
                cam: match[1]!,
                url: fname,
                token,
                accessToken,
                isStream,
            });
        } else {
            const parts = fname.split("/");
            const name = parts[parts.length - 1]?.split(".")[0] ?? fname;
            entries.push({ title: name, cam: null, url: fname, token, accessToken, isStream });
        }
    }

    entries.sort((a, b) => a.url.localeCompare(b.url));

    // Deduplicate cam labels — only show cam on first occurrence
    if (entries.length > 1) {
        let lastCam = entries[0]!.cam;
        for (let i = 1; i < entries.length; i++) {
            if (entries[i]!.cam === lastCam) {
                entries[i] = { ...entries[i]!, cam: null };
            } else {
                lastCam = entries[i]!.cam;
            }
        }
    }

    return entries;
}

function btnStyle(bg?: string): React.CSSProperties {
    return {
        padding: "var(--space-1) var(--space-3)",
        borderRadius: "var(--radius-1)",
        border: "1px solid var(--color-border-default)",
        background: bg ?? "var(--color-bg-muted)",
        color: bg ? "#fff" : undefined,
        cursor: "pointer",
        fontSize: "var(--text-sm)",
    };
}
